import { useState } from "react";

const PRESETS = [
  { label: "3 mg", qty: 3, unit: "mg" },
  { label: "5 mg", qty: 5, unit: "mg" },
  { label: "10 mg", qty: 10, unit: "mg" },
  { label: "50 mg", qty: 50, unit: "mg" },
  { label: "5000 IU", qty: 5000, unit: "IU" },
  { label: "10000 IU", qty: 10000, unit: "IU" },
];

export default function CalculatorView({ setView }) {
  const [vialQty, setVialQty] = useState("");
  const [unit, setUnit] = useState("mg");
  const [bacVolume, setBacVolume] = useState("");
  const [desiredDose, setDesiredDose] = useState("");

  const vialNum = parseFloat(vialQty) || 0;
  const bacNum = parseFloat(bacVolume) || 0;
  const doseNum = parseFloat(desiredDose) || 0;
  const concentration = bacNum > 0 ? vialNum / bacNum : 0;
  const injectionVolume = concentration > 0 ? doseNum / concentration : 0;

  const applyPreset = (p) => {
    setVialQty(String(p.qty));
    setUnit(p.unit);
  };

  const inputStyle = {
    width: "100%", padding: "12px 14px", fontSize: "16px",
    background: "var(--surface)", color: "var(--text)",
    border: "1px solid var(--border)", borderRadius: 4,
    fontFamily: "var(--font-mono)", outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block", fontFamily: "var(--font-mono)", fontSize: "11px",
    letterSpacing: "0.1em", textTransform: "uppercase",
    color: "var(--text-muted)", marginBottom: 8,
  };

  const cardStyle = {
    background: "var(--surface)", border: "1px solid var(--border)",
    borderRadius: 6, padding: 24,
  };

  const resultReady = vialNum > 0 && bacNum > 0 && doseNum > 0;

  return (
    <div className="fade-up" style={{ maxWidth: 560, margin: "0 auto", padding: "120px 20px 80px" }}>
      <div style={{ marginBottom: 32 }}>
        <p style={{
          fontFamily: "var(--font-mono)", fontSize: "11px", letterSpacing: "0.22em",
          color: "var(--amber)", textTransform: "uppercase", marginBottom: 12,
          display: "inline-block", padding: "6px 14px",
          background: "rgba(0, 212, 170, 0.08)", border: "1px solid rgba(0, 212, 170, 0.2)",
          borderRadius: 20,
        }}>Tool</p>
        <h1 style={{ fontSize: "32px", letterSpacing: "-0.02em", marginBottom: 8 }}>
          Reconstitution Calculator
        </h1>
        <p style={{ color: "var(--text-dim)", fontSize: "15px", lineHeight: 1.6 }}>
          Calculate injection volume based on vial content, BAC water volume, and desired dose.
        </p>
      </div>

      {/* Presets */}
      <div style={{ marginBottom: 24 }}>
        <span style={labelStyle}>Quick Presets</span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {PRESETS.map((p) => (
            <button key={p.label} onClick={() => applyPreset(p)} style={{
              padding: "6px 14px", fontSize: "12px", fontFamily: "var(--font-mono)",
              background: (vialQty === String(p.qty) && unit === p.unit) ? "rgba(0,212,170,0.12)" : "transparent",
              color: (vialQty === String(p.qty) && unit === p.unit) ? "var(--amber)" : "var(--text-dim)",
              border: (vialQty === String(p.qty) && unit === p.unit) ? "1px solid rgba(0,212,170,0.3)" : "1px solid var(--border)",
              borderRadius: 4, cursor: "pointer", transition: "all 0.15s",
              letterSpacing: "0.03em",
            }}>{p.label}</button>
          ))}
        </div>
      </div>

      {/* Inputs */}
      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Vial quantity + unit */}
          <div>
            <label style={labelStyle}>Vial Content</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="number" inputMode="decimal" placeholder="e.g. 5"
                value={vialQty} onChange={(e) => setVialQty(e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
                onFocus={(e) => e.target.style.borderColor = "var(--amber)"}
                onBlur={(e) => e.target.style.borderColor = "var(--border)"}
              />
              <div style={{ display: "flex", borderRadius: 4, overflow: "hidden", border: "1px solid var(--border)", flexShrink: 0 }}>
                {["mg", "IU"].map((u) => (
                  <button key={u} onClick={() => setUnit(u)} style={{
                    padding: "12px 16px", fontSize: "13px", fontFamily: "var(--font-mono)",
                    fontWeight: 700, letterSpacing: "0.05em", cursor: "pointer",
                    background: unit === u ? "var(--amber)" : "var(--surface)",
                    color: unit === u ? "#111" : "var(--text-dim)",
                    border: "none", transition: "all 0.15s",
                  }}>{u}</button>
                ))}
              </div>
            </div>
          </div>

          {/* BAC water volume */}
          <div>
            <label style={labelStyle}>BAC Water Added (mL)</label>
            <input
              type="number" inputMode="decimal" placeholder="e.g. 2"
              value={bacVolume} onChange={(e) => setBacVolume(e.target.value)}
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = "var(--amber)"}
              onBlur={(e) => e.target.style.borderColor = "var(--border)"}
            />
          </div>

          {/* Desired dose */}
          <div>
            <label style={labelStyle}>Desired Dose ({unit})</label>
            <input
              type="number" inputMode="decimal" placeholder={`e.g. ${unit === "IU" ? "250" : "0.5"}`}
              value={desiredDose} onChange={(e) => setDesiredDose(e.target.value)}
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = "var(--amber)"}
              onBlur={(e) => e.target.style.borderColor = "var(--border)"}
            />
          </div>
        </div>
      </div>

      {/* Results */}
      <div style={{
        ...cardStyle,
        borderColor: resultReady ? "rgba(0,212,170,0.3)" : "var(--border)",
        transition: "border-color 0.3s",
      }}>
        {/* Concentration */}
        <div style={{ marginBottom: 20 }}>
          <span style={labelStyle}>Concentration</span>
          <p style={{
            fontSize: "24px", fontFamily: "var(--font-mono)", fontWeight: 700,
            color: concentration > 0 ? "var(--text)" : "var(--text-muted)",
          }}>
            {concentration > 0
              ? `${Number(concentration.toFixed(4))} ${unit}/mL`
              : "—"
            }
          </p>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "var(--border)", margin: "0 0 20px" }} />

        {/* Injection volume */}
        <div>
          <span style={labelStyle}>Inject This Much</span>
          <div style={{
            display: "flex", alignItems: "baseline", gap: 16, flexWrap: "wrap",
            marginBottom: 8,
          }}>
            <p style={{
              fontSize: "48px", fontFamily: "var(--font-mono)", fontWeight: 700,
              color: resultReady ? "var(--amber)" : "var(--text-muted)",
              letterSpacing: "-0.02em", lineHeight: 1,
            }}>
              {resultReady
                ? Number(injectionVolume.toFixed(3))
                : "—"
              }
            </p>
            {resultReady && (
              <span style={{
                fontSize: "20px", fontFamily: "var(--font-mono)", fontWeight: 700,
                color: "var(--text-dim)",
              }}>mL</span>
            )}
          </div>
          {resultReady && injectionVolume > 0 && (
            <div style={{
              background: "rgba(0,212,170,0.08)", border: "1px solid rgba(0,212,170,0.2)",
              borderRadius: 6, padding: "14px 18px", marginTop: 12,
            }}>
              <p style={{
                fontFamily: "var(--font-mono)", fontSize: "20px", fontWeight: 700,
                color: "var(--amber)", marginBottom: 4,
              }}>
                {Number((injectionVolume * 100).toFixed(1))} units
              </p>
              <p style={{
                fontFamily: "var(--font-mono)", fontSize: "12px",
                color: "var(--text-dim)",
              }}>
                on a standard 1 mL / 100-unit insulin syringe
              </p>
            </div>
          )}
          {resultReady && doseNum > vialNum && (
            <p style={{
              fontFamily: "var(--font-mono)", fontSize: "12px",
              color: "var(--red)", marginTop: 10,
            }}>
              ⚠ Desired dose exceeds vial content ({vialNum} {unit})
            </p>
          )}
          {resultReady && injectionVolume > bacNum && (
            <p style={{
              fontFamily: "var(--font-mono)", fontSize: "12px",
              color: "var(--red)", marginTop: 6,
            }}>
              ⚠ Injection volume exceeds total reconstituted volume ({bacNum} mL)
            </p>
          )}
        </div>
      </div>

      {/* Doses per vial */}
      {resultReady && doseNum > 0 && doseNum <= vialNum && (
        <div style={{ ...cardStyle, marginTop: 20 }}>
          <span style={labelStyle}>Doses Per Vial</span>
          <p style={{ fontSize: "24px", fontFamily: "var(--font-mono)", fontWeight: 700, color: "var(--text)" }}>
            {Math.floor(vialNum / doseNum)} doses
          </p>
          {vialNum % doseNum !== 0 && (
            <p style={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "var(--text-dim)", marginTop: 4 }}>
              + {Number((vialNum % doseNum).toFixed(2))} {unit} remainder
            </p>
          )}
        </div>
      )}

      {/* Back to shop */}
      <div style={{ marginTop: 32, textAlign: "center" }}>
        <button onClick={() => setView("shop")} style={{
          fontFamily: "var(--font-mono)", fontSize: "12px",
          color: "var(--text-dim)", background: "none", border: "none",
          cursor: "pointer", letterSpacing: "0.05em",
          transition: "color 0.15s",
        }}
          onMouseEnter={(e) => e.target.style.color = "var(--amber)"}
          onMouseLeave={(e) => e.target.style.color = "var(--text-dim)"}
        >← Back to Shop</button>
      </div>
    </div>
  );
}
